import { createContext, useContext, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { reducedMotion } from '~/deck2/case/motion';
import styles from './stage.module.css';

/** Every scene is drawn on this canvas, as in the Paper file, and scaled to the width it gets. */
export const SCENE_W = 620;
/** The scale a scene is drawn at when the page has the room for it. */
const FULL_SCALE = 1.1823;

/** How much of a scene has to be in view before it plays. */
const IN_VIEW = 0.6;
/** How long a scene rests on its last frame before it plays again, and how long it takes to fade out. */
const HOLD_MS = 2500;
const FADE_MS = 300;

/**
 * Plays the scene again: whoever draws it starts it over, fresh, by mounting it anew. Without one, a scene
 * plays once and keeps its last frame.
 */
export const SceneLoop = createContext<(() => void) | null>(null);

/**
 * One "How it works" scene. It goes live as soon as most of it is on screen, and plays:
 * `marks` are the moments (ms after going live) the scene moves on, and `children` gets how many have passed.
 * It rests on its last frame, fades and plays again, though never under someone's pointer, who may be trying it
 * out. With reduced motion it skips straight to the last frame and stays there.
 */
export function Stage({
  label,
  marks,
  wide = SCENE_W,
  className,
  onPointerEnter,
  onPointerLeave,
  children,
}: {
  label: string;
  marks: readonly number[];
  /**
   * For a scene with more to lay out side by side: how wide its canvas may grow, in canvas units, when the
   * page has the room. It is never narrower than SCENE_W, and stays the same height.
   */
  wide?: number;
  className?: string;
  onPointerEnter?: () => void;
  onPointerLeave?: () => void;
  children: (step: number) => ReactNode;
}) {
  const frame = useRef<HTMLDivElement>(null);
  const [drawn, setDrawn] = useState<{ width: number; canvas: number } | null>(null);
  const [live, setLive] = useState(false);
  const [still, setStill] = useState(false);
  const [step, setStep] = useState(0);
  const again = useContext(SceneLoop);
  const [ended, setEnded] = useState(false);
  const [pointerOn, setPointerOn] = useState(false);
  const fading = ended && !pointerOn;

  useEffect(() => {
    const el = frame.current;
    if (!el) return;
    setStill(reducedMotion());
    // The canvas takes what room the page gives, up to `wide`, and is scaled to the frame that results.
    const resize = new ResizeObserver(([entry]) => {
      const room = entry.contentRect.width;
      const canvas = Math.round(Math.min(wide, Math.max(SCENE_W, room / FULL_SCALE)));
      setDrawn({ width: Math.min(room, canvas * FULL_SCALE), canvas });
    });
    // It plays the moment most of it is in view, even mid-scroll.
    const view = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio < IN_VIEW) return;
        setLive(true);
        view.disconnect();
      },
      { threshold: [0, IN_VIEW, 1] },
    );
    resize.observe(el.parentElement ?? el);
    view.observe(el);
    return () => {
      resize.disconnect();
      view.disconnect();
    };
  }, [wide]);

  useEffect(() => {
    if (!live) return;
    if (still) {
      setStep(marks.length);
      return;
    }
    const timers = marks.map((ms, i) => window.setTimeout(() => setStep(i + 1), ms));
    if (again) timers.push(window.setTimeout(() => setEnded(true), (marks[marks.length - 1] ?? 0) + HOLD_MS));
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [live, still, marks, again]);

  // Once it has faded out, it starts over. A pointer coming back during the fade brings it back instead.
  useEffect(() => {
    if (!fading || !again) return;
    const timer = window.setTimeout(again, FADE_MS);
    return () => window.clearTimeout(timer);
  }, [fading, again]);

  return (
    <div
      ref={frame}
      role="group"
      aria-label={label}
      // Scenes style their entrances off `[data-live]`, since their CSS modules can't see this one's classes.
      data-live={live || undefined}
      className={`${styles.frame} ${still ? styles.still : ''} ${fading ? styles.fading : ''} ${className ?? ''}`}
      style={drawn ? ({ '--scale': drawn.width / drawn.canvas, '--w': drawn.canvas } as CSSProperties) : undefined}
      onPointerEnter={() => {
        setPointerOn(true);
        onPointerEnter?.();
      }}
      onPointerLeave={() => {
        setPointerOn(false);
        onPointerLeave?.();
      }}
    >
      <div className={styles.canvas}>{children(step)}</div>
    </div>
  );
}

/** The cursor that shows the scene being used. It glides to `at` and dips while `pressed`. */
export function Cursor({ at, shown, pressed }: { at: [number, number]; shown: boolean; pressed?: boolean }) {
  return (
    <div
      className={`${styles.cursor} ${shown ? styles.cursorShown : ''} ${pressed ? styles.cursorPressed : ''}`}
      style={{ translate: `${at[0]}px ${at[1]}px` }}
      aria-hidden
    >
      <CursorArrow />
    </div>
  );
}

/** How quickly entrances run against the delays the scenes were drawn with; under 1 is quicker. */
const PACE = 0.7;

/** Deck's cursor, as drawn in Paper: ink with a white edge. Its tip is at (7, 7) at full size (28 by 29). */
export function CursorArrow({ size = 28, className }: { size?: number; className?: string }) {
  return (
    <svg className={className} width={size} height={(size * 29) / 28} viewBox="0 0 28 29" aria-hidden>
      <path
        d="M11.892 23.711L7.334 7.72C7.098 6.894 7.956 6.181 8.736 6.554L23.838 13.779C24.642 14.163 24.581 15.316 23.742 15.613L17.531 17.814C17.306 17.894 17.116 18.051 16.998 18.257L13.732 23.936C13.291 24.703 12.135 24.562 11.892 23.711Z"
        fill="var(--color-text-primary, #292929)"
      />
      <path
        d="M6.851 7.854C6.498 6.616 7.784 5.547 8.953 6.106L24.056 13.331C25.261 13.908 25.17 15.636 23.912 16.082L17.701 18.282C17.588 18.322 17.494 18.401 17.435 18.504L14.168 24.183C13.507 25.333 11.773 25.121 11.409 23.846L6.851 7.854Z"
        fill="none"
        stroke="#ffffff"
      />
    </svg>
  );
}

/** Delay for one element's entrance, read by the scene's CSS as `--d`. */
export const after = (ms: number) => ({ '--d': `${Math.round(ms * PACE)}ms` }) as CSSProperties;
